import React, { useState, useMemo } from 'react';
import Dropdown from '../Dropdown';
// Utils 
import { getTotalsBook, getMoneylineBook, getSpreadBook, formatOddsAndLines, getSportsPeriodOptions } from '../../../utils/betting';

const LinesOverview = ({ game, sport, homeTeam, awayTeam }) => {
    const [selectedPeriod, setSelectedPeriod] = useState('full');

    const sportPeriodOptions = useMemo(() => getSportsPeriodOptions(sport), [sport]);

    // Get first book's most recent totals line
    const totalsLine = useMemo(
        () => getTotalsBook(game, sport, selectedPeriod, 'first'),
        [game, sport, selectedPeriod]
    );

    // Get first book's most recent moneyline
    const moneylineLine = useMemo(
        () => getMoneylineBook(game, sport, selectedPeriod, 'first'),
        [game, sport, selectedPeriod]
    );
    
    // Get first book's most recent spread/runline
    const spreadLine = useMemo(
        () => getSpreadBook(game, sport, selectedPeriod, 'first'),
        [game, sport, selectedPeriod]
    );

    const spreadLabel = sport === 'mlb' ? 'Runline' : 'Spread';

    // If no lines are available, show unavailable message
    if (!totalsLine && !moneylineLine && !spreadLine) {
        return (
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                        Lines Overview
                    </h2>
                    <div className="w-full sm:w-auto">
                        <Dropdown
                            title="Select Period"
                            items={sportPeriodOptions}
                            activeItem={selectedPeriod}
                            onSelect={setSelectedPeriod}
                            size="md"
                            variant="default"
                        />
                    </div>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 my-4">
                    No lines available
                </div>
            </div>
        );
    }

    return (
        <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6">
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                    Lines Overview
                </h2>
                <div className="w-full sm:w-auto">
                    <Dropdown
                        title="Select Period"
                        items={sportPeriodOptions}
                        activeItem={selectedPeriod}
                        onSelect={setSelectedPeriod}
                        size="md"
                        variant="default"
                    />
                </div>
            </div>
            <div className="space-y-3 flex flex-col justify-center items-center">
                {/* Moneyline */}
                {moneylineLine ? (
                    <div className="w-full xl:w-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                Moneyline
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {moneylineLine.book}
                            </span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-12">{homeTeam}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOddsAndLines(moneylineLine.home)}
                                </span>
                            </div>
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-12">{awayTeam}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOddsAndLines(moneylineLine.away)}
                                </span>
                            </div>
                        </div>
                    </div>
                )
            
                : (
                    <div className="w-full xl:w-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <div>        
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    Moneyline
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-500 dark:text-gray-400">
                                    No lines available
                                </span>
                            </div>
                    </div>
                )}

                {/* Spread/Runline */}
                {spreadLine ? (
                    <div className="w-full xl:w-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {spreadLabel}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {spreadLine.book}
                            </span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-12">{homeTeam}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOddsAndLines(spreadLine.home.points)} ({formatOddsAndLines(spreadLine.home.odds)})
                                </span>
                            </div>
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-12">{awayTeam}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOddsAndLines(spreadLine.away.points)} ({formatOddsAndLines(spreadLine.away.odds)})
                                </span>
                            </div>
                        </div>
                    </div>
                )
                : (
                    <div className="w-full xl:w-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {spreadLabel}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-500 dark:text-gray-400">
                                No lines available
                            </span>
                        </div>
                    </div>
                )}

                {/* Totals */}
                {totalsLine ? (
                    <div className="w-full xl:w-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                Totals
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {totalsLine.book}
                            </span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-16">O {totalsLine.line}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOddsAndLines(totalsLine.over)}
                                </span>
                            </div>
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-16">U {totalsLine.line}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOddsAndLines(totalsLine.under)}
                                </span>
                            </div>
                        </div>
                    </div>
                )
                : (
                    <div className="w-full xl:w-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                Totals
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-500 dark:text-gray-400">
                                No lines available
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LinesOverview;
